import React, { useState, useEffect, useRef } from "react"
import { Text, Box, Flex, Grid, GridItem } from "@chakra-ui/react"
import { Logger } from "lib/logger"
import { createMarkup } from "utils/createMarkup"
import { useSegment } from "tracking"
import { colors } from "lib/theme"
import type { VariantTrialType } from "types"
import Image from "next/image"
import { withErrorBoundary } from "react-error-boundary"
import { useInView } from "react-intersection-observer"
import { dispatch } from "lib/events"
import {
  CopyBox,
  Copy,
  BeforeAfterContainer,
  BeforeAfterBox,
  BeforeAfterImageContainer,
  BeforeImageContainer,
  SliderThumb,
  SliderThumbLeftArrow,
  SliderThumbRightArrow,
} from "./RealResults.styles"
import { checkIfCanDrag } from "./utils"

interface Props {
  trials: VariantTrialType
}

const minSliderX = 15
const sliderDefaultValue = 30

const imageStyles = {
  objectFit: "cover",
  objectPosition: "center",
  position: "absolute",
  top: "0",
  left: 0,
  bottom: 0,
  right: 0,
  userSelect: "none",
  WebkitTouchCallout: "none",
} as React.CSSProperties

export const RealResultsComponent: React.FC<Props> = ({ trials }) => {
  const { results, afterImage, beforeImage, sourceOfTrialResults, title } =
    trials
  const beforeAfterRef = useRef<HTMLDivElement>(null)
  const sliderThumbRef = useRef<HTMLDivElement>(null)
  const [sliderValue, setSliderValue] = useState(sliderDefaultValue)
  const [percentageWidth, setPercentageWidth] = useState(50)
  const hasTouchStarted = useRef(false)
  const { track } = useSegment()

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 1,
  })

  useEffect(() => {
    if (inView) {
      dispatch("page element viewed", {
        target: "PDP Real Results",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  useEffect(() => {
    if (!hasTouchStarted.current) {
      return
    }
    const timer = setTimeout(() => {
      track({
        event: "Page Element Clicked",
        properties: {
          module_content: `Real Results`,
          module_content_details: `${sliderValue}%`,
        },
      })
    }, 500)
    return () => clearTimeout(timer)
  }, [sliderValue, track])

  useEffect(() => {
    let canDrag = false
    const currentRef = beforeAfterRef.current
    const sliderThumb = sliderThumbRef.current

    if (!currentRef || !sliderThumb) return

    const onResize = () => {
      setPercentageWidth(currentRef.offsetWidth)
    }

    window.addEventListener("resize", onResize)
    onResize()

    const onTouchMove = (e: TouchEvent) => {
      const cWidth = currentRef.offsetWidth
      const currentPoint = e.changedTouches[0].clientX

      const startOfDiv = currentRef.offsetLeft

      const modifiedCurrentPoint = currentPoint - startOfDiv

      if (
        modifiedCurrentPoint > minSliderX &&
        modifiedCurrentPoint < cWidth - minSliderX
      ) {
        const pos = (modifiedCurrentPoint * 100) / cWidth
        setSliderValue(pos)
      }
    }

    const onTouchStart = (e: TouchEvent) => {
      canDrag = checkIfCanDrag(e, sliderThumb.getBoundingClientRect())
      if (!canDrag) return
      currentRef.style.cursor = "grabbing"
      hasTouchStarted.current = true
      currentRef.addEventListener("touchmove", onTouchMove)
      onTouchMove(e)
    }

    currentRef.addEventListener("touchstart", onTouchStart)

    const onMouseDown = (e: MouseEvent) => {
      canDrag = checkIfCanDrag(e, sliderThumb.getBoundingClientRect())
      if (!canDrag) return
      currentRef.style.cursor = "grabbing"
      hasTouchStarted.current = true
      onMouseMove(e)
    }

    const onMouseUp = () => {
      canDrag = false
      currentRef.style.cursor = "grab"
      currentRef.removeEventListener("touchmove", onTouchMove)
    }

    currentRef.addEventListener("mousedown", onMouseDown)

    window.addEventListener("mouseup", onMouseUp)
    window.addEventListener("touchend", onMouseUp)
    window.addEventListener("touchcancel", onMouseUp)

    const onMouseMove = (e: MouseEvent) => {
      if (!canDrag) return
      const cWidth = currentRef.offsetWidth
      const w = (e.offsetX * 100) / cWidth

      if (e.offsetX > minSliderX && e.offsetX < cWidth - minSliderX) {
        setSliderValue(w)
      }
    }

    currentRef.addEventListener("mousemove", onMouseMove)

    return () => {
      currentRef.removeEventListener("mousemove", onMouseMove)
      currentRef.removeEventListener("mousedown", onMouseDown)
      currentRef.removeEventListener("touchstart", onTouchStart)
      currentRef.removeEventListener("touchmove", onTouchMove)
      window.removeEventListener("resize", onResize)
      window.removeEventListener("mouseup", onMouseUp)
      window.removeEventListener("touchend", onMouseUp)
      window.removeEventListener("touchcancel", onMouseUp)
    }
  }, [])

  return (
    <Box
      m="0"
      data-testid="before-after-results"
      w="full"
      ref={ref}
      id="results"
      scrollMarginTop={{ base: "110px", lg: "150px" }}
    >
      {title && (
        <Text
          variant="apercu"
          mt={1}
          mb="1.5rem"
          fontSize={{ base: "1.25rem", md: 9 }}
        >
          {title}
        </Text>
      )}
      <Grid
        templateColumns={{ base: "repeat(1, 1fr)", lg: "repeat(2, 1fr)" }}
        gap={{ md: -1 }}
      >
        <GridItem>
          <BeforeAfterContainer>
            <BeforeAfterBox ref={beforeAfterRef}>
              <BeforeAfterImageContainer width={`${percentageWidth ?? 100}px`}>
                <Image
                  alt="after"
                  src={afterImage?.thumbnails?.size_OTF_BETA ?? ""}
                  draggable="false"
                  style={imageStyles}
                  fill
                />
                <CopyBox
                  right={{
                    base: "0.5rem",
                    md: "1.875rem",
                    lg: "1.25rem",
                  }}
                  left="auto"
                >
                  <Copy variant="acuminLight">after</Copy>
                </CopyBox>
              </BeforeAfterImageContainer>
              <BeforeImageContainer width={`${sliderValue ?? 100}%`}>
                <BeforeAfterImageContainer
                  width={`${percentageWidth ?? 100}px`}
                >
                  <Image
                    alt="before"
                    src={beforeImage?.thumbnails?.size_OTF_BETA ?? ""}
                    draggable="false"
                    style={imageStyles}
                    fill
                  />
                  <CopyBox
                    left={{
                      base: "0.5rem",
                      md: "1.875rem",
                      lg: "1.25rem",
                    }}
                  >
                    <Copy variant="acuminLight">before</Copy>
                  </CopyBox>
                </BeforeAfterImageContainer>
              </BeforeImageContainer>
              <SliderThumb left={`${sliderValue}%`}>
                <SliderThumbLeftArrow as="span" />
                <SliderThumbRightArrow as="span" />
                <Box
                  ref={sliderThumbRef}
                  position="absolute"
                  zIndex={3}
                  width="55px"
                  height="55px"
                  top="-13px"
                  left="-13px"
                  background="transparent"
                />
              </SliderThumb>
            </BeforeAfterBox>
          </BeforeAfterContainer>
        </GridItem>
        <GridItem px={{ lg: 3 }} pt={{ base: "1.5rem", md: "0" }}>
          <Flex direction="column" h="100%" justifyContent="center">
            {sourceOfTrialResults && (
              <Text
                variant="acuminRegular"
                mt={{ base: 0, md: 1, lg: 0 }}
                fontSize={4}
                lineHeight={3}
                mb={1}
              >
                {sourceOfTrialResults}
              </Text>
            )}
            {results?.map((result) => (
              <Grid
                templateColumns={{
                  base: "repeat(6, 2fr)",
                  md: "repeat(9, 2fr)",
                  lg: "repeat(5, 2fr)",
                }}
                borderTop={`1px solid ${colors.borderTertiary}`}
                py={{ base: "0.62rem" }}
                key={`${result?.value}-${result?.label}`}
              >
                {result?.label && (
                  <GridItem colSpan={1}>
                    <Text
                      mt={0}
                      fontSize={2}
                      variant="acuminRegular"
                      dangerouslySetInnerHTML={createMarkup(result.label)}
                    />
                  </GridItem>
                )}
                {result?.value && (
                  <GridItem colSpan={{ base: 5, md: 8, lg: 4 }}>
                    <Text
                      mt={0}
                      variant="acuminLight"
                      dangerouslySetInnerHTML={createMarkup(result.value)}
                    />
                  </GridItem>
                )}
              </Grid>
            ))}
          </Flex>
        </GridItem>
      </Grid>
    </Box>
  )
}

export const RealResults = withErrorBoundary(RealResultsComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`BeforeAfter failed to load. ${error}`)
  },
})
